/**
 * Alpha DNA teaser: full-screen 3D vault (outside the dashboard shell).
 * Black screen with the Alpha mark brightening as assets load -> the mark
 * switches off like a light -> the camera glides in from black, the lights
 * strike up over the card -> "Alpha DNA / Coming soon" with the waitlist
 * email form. The card is one fixed design: Stealth metal, Gold tier, the Alpha
 * symbol, cut corners. Back returns to /dashboard.
 */
import './AlphaDna.css';

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlphaMark from './AlphaMark';
import { DEFAULT_CONFIG } from './cardDesign';
import { createVault } from './scene';
import WaitlistForm, { JoinedNote } from './WaitlistForm';
import { useLaunchSubscription } from '../../store/api/productLaunchApi';

const LOGO_OFF_MS = 950; // matches the adna-switch-off keyframes

export default function AlphaDna() {
  const hostRef = useRef(null);
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('loading');
  const [joined, setJoined] = useState(false);
  // Already on the list from an earlier visit, or just now.
  const subscription = useLaunchSubscription('alpha_dna');
  const onList = joined || Boolean(subscription);

  // nothing else in the app sets the title, so hand the old one back on the way out
  useEffect(() => {
    const prev = document.title;
    document.title = 'Alpha DNA · Alpha Capital';
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let switchTimer = 0;
    const vault = createVault(
      host,
      {
        onProgress: (f) => setProgress((p) => Math.max(p, f)),
        onReady: () => {
          setProgress(1);
          setPhase('switching');
          // the mark flickers off, then the black lifts as the camera starts moving
          switchTimer = window.setTimeout(
            () => {
              setPhase('intro');
              vault.start();
            },
            reducedMotion ? 0 : LOGO_OFF_MS,
          );
        },
        onIntroDone: () => setPhase('idle'),
        onError: (err) => {
          console.error('[alpha-dna] failed to load the vault', err);
          setPhase('error');
        },
      },
      { reducedMotion, initialConfig: DEFAULT_CONFIG },
    );
    return () => {
      window.clearTimeout(switchTimer);
      vault.dispose();
    };
  }, []);

  return (
    <div className={`adna-root is-${phase}`}>
      <div className="adna-stage" ref={hostRef} />
      <div className="adna-vignette" aria-hidden="true" />

      <button type="button" className="adna-back" onClick={() => navigate('/dashboard')}>
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M9.75 3.5 5.25 8l4.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Dashboard</span>
      </button>

      <div className="adna-title" aria-hidden={phase !== 'idle'} inert={phase !== 'idle' ? '' : undefined}>
        <span className="adna-soon">
          <AlphaMark className="adna-soon-mark" />
          Coming soon
        </span>
        <h1 aria-label="Alpha DNA">
          ALPHA <span className="adna-accent">DNA</span>
        </h1>
        <p className="adna-tagline">Your trading. Your rating. Your reputation.</p>
        <p className="adna-lede">
          Your own trader card, built on your performance. Build your rating, compete in your division and unlock
          exclusive rewards.
        </p>
        {onList ? (
          <JoinedNote>You’re on the list. We’ll let you know when Alpha DNA launches.</JoinedNote>
        ) : (
          <WaitlistForm
            disabled={phase !== 'idle'}
            placeholder="Enter your email"
            submitLabel="Be first to know"
            busyLabel="Sending…"
            note="Get notified when Alpha DNA launches."
            onJoined={() => setJoined(true)}
          />
        )}
      </div>

      <div
        className="adna-loader"
        role="status"
        aria-live="polite"
        aria-hidden={phase === 'intro' || phase === 'idle'}
        style={{ '--adna-progress': progress }}
      >
        <div className="adna-loader-mark">
          <AlphaMark className="adna-loader-logo" />
          <AlphaMark className="adna-loader-logo adna-loader-logo--glow" />
        </div>
        {phase === 'error' ? (
          <p className="adna-loader-text">The vault couldn’t load. Check your connection and try again.</p>
        ) : (
          <span className="adna-sr-only">Loading Alpha DNA, {Math.round(progress * 100)}%</span>
        )}
      </div>
    </div>
  );
}
